/** Saved versions (spec §3.3): the inline "Save version" form and the versions list. */

import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { SavedVersion } from "../backends/types";

const WHEN = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function versionWhen(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? "" : WHEN.format(at);
}

export function SaveVersionForm({
  defaultName,
  busy,
  onSave,
  onCancel,
}: {
  defaultName: string;
  busy: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const canSave = !busy && name.trim().length > 0;
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter" && canSave) {
      e.preventDefault();
      onSave(name);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  }
  return (
    <form
      className="vsave"
      aria-label="Save a version"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) onSave(name);
      }}
    >
      <input
        ref={ref}
        className="vsave__input"
        aria-label="Version name"
        maxLength={80}
        value={name}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <button type="submit" className="btn btn--primary btn--sm" disabled={!canSave}>
        {busy ? "Saving…" : "Save"}
      </button>
      <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
    </form>
  );
}

export function VersionsMenu({
  versions,
  loaded,
  busy,
  picking,
  otherConversation,
  onCompare,
  onDelete,
  onClose,
}: {
  versions: readonly SavedVersion[];
  loaded: boolean;
  busy: boolean;
  /** Opened from "Compare with…": the heading asks the user to pick. */
  picking: boolean;
  /** True for versions saved in a different conversation than the one in this pane. */
  otherConversation: (v: SavedVersion) => boolean;
  onCompare: (versionId: string) => void;
  onDelete: (versionId: string) => void;
  onClose: () => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  return (
    <section
      className="versions"
      aria-label="Saved versions"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <header className="versions__head">
        <span className="group__title">{picking ? "Compare with which version?" : "Saved versions"}</span>
        <button type="button" className="link-btn" aria-label="Close versions" onClick={onClose}>
          Close
        </button>
      </header>
      {!loaded ? <p className="note">Loading…</p> : null}
      {loaded && versions.length === 0 ? (
        <p className="note">No saved versions yet. Save one now, then ask Ella to compare the document with it later.</p>
      ) : null}
      {versions.length ? (
        <ul className="versions__list">
          {versions.map((v) => (
            <li key={v.versionId} className="version">
              <div className="version__main">
                <span className="version__name">{v.name}</span>
                <span className="version__meta">
                  {[versionWhen(v.createdAt), `${v.paragraphCount.toLocaleString("en-US")} ¶`, otherConversation(v) ? "earlier chat" : ""]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
              {confirming === v.versionId ? (
                <span className="version__actions">
                  <span className="small">Delete?</span>
                  <button
                    type="button"
                    className="link-btn link-btn--danger"
                    disabled={busy}
                    onClick={() => {
                      setConfirming(null);
                      onDelete(v.versionId);
                    }}
                  >
                    Delete
                  </button>
                  <button type="button" className="link-btn" onClick={() => setConfirming(null)}>
                    Keep
                  </button>
                </span>
              ) : (
                <span className="version__actions">
                  <button type="button" className="btn btn--sm" disabled={busy} onClick={() => onCompare(v.versionId)}>
                    Compare
                  </button>
                  {picking ? null : (
                    <button type="button" className="link-btn version__delete" disabled={busy} onClick={() => setConfirming(v.versionId)}>
                      Delete
                    </button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
